import {
  MAX_PASSWORD_LENGTH,
  allRulesSatisfied,
  validateAge,
  type PasswordRules,
} from '@wineryxplorer/shared';

/**
 * Pure field validators for the sign-in / sign-up form. Each returns the
 * error message to display, or an empty string when the field is valid (an
 * empty field is treated as "not yet an error", same as before extraction).
 */

export const getEmailError = (value: string): string => {
  if (value.length === 0) return '';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value) ? '' : 'Please enter a valid email address';
};

export const getNameError = (value: string): string => {
  if (value.length === 0) return '';
  return value.trim().length < 2 ? 'Name must be at least 2 characters' : '';
};

export const getUserNameError = (value: string): string => {
  if (value.length === 0) return '';
  const userNameRegex = /^[a-zA-Z0-9_]+$/;
  if (!userNameRegex.test(value)) {
    return 'Only letters, numbers, and underscores allowed';
  }
  if (value.length < 3) {
    return 'Username must be at least 3 characters';
  }
  return '';
};

export const getPasswordError = (value: string, rules: PasswordRules): string => {
  if (value.length === 0) return '';
  if (value.length > MAX_PASSWORD_LENGTH) {
    return `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`;
  }
  if (!allRulesSatisfied(rules)) {
    return 'Password does not meet all requirements';
  }
  return '';
};

/**
 * Date of birth, judged against the country the person selected. Mirrors the
 * server check in auth.register; the server one is the authority, this is only
 * here so someone learns the answer before submitting the form.
 */
export const getBirthdayError = (value: string, country: string): string => {
  if (value.length === 0) return '';

  // <input type="date"> yields YYYY-MM-DD, which Date parses as UTC midnight.
  // Building the date from its parts keeps it in local time, so a birthday
  // never slides a day backwards for anyone west of Greenwich.
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return 'Please enter a complete date of birth.';

  const birthday = new Date(year, month - 1, day, 12, 0, 0);
  if (
    birthday.getFullYear() !== year ||
    birthday.getMonth() !== month - 1 ||
    birthday.getDate() !== day
  ) {
    return 'Please enter a complete date of birth.';
  }

  if (birthday.getTime() > Date.now()) {
    return 'Your date of birth cannot be in the future.';
  }

  return validateAge(birthday, country) ?? '';
};

/** The local-time Date a YYYY-MM-DD field value stands for, for submission. */
export const birthdayToDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
};
